import { useState } from "react";
import type { FormEvent } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import JsonLd from "@/components/base/JsonLd";

const site = "https://ymdbo14.cn";

const values = [
  { icon: "ri-price-tag-3-line", num: "1200+", label: "收录漫画", desc: "六大分类持续收录扩充" },
  { icon: "ri-list-check-2", num: "80万+", label: "章节目录", desc: "漫画目录随源更新同步" },
  { icon: "ri-time-line", num: "24h", label: "持续更新", desc: "连载新话第一时间同步" },
  { icon: "ri-heart-3-line", num: "500万+", label: "读者信赖", desc: "累计服务漫画爱好者" },
];

const testimonials = [
  {
    name: "阿林",
    role: "热血漫忠实读者",
    text: "漫画网的分类做得特别清晰，想找热血漫画直接进列表就行，更新也快，追漫体验很舒服。",
  },
  {
    name: "小柚",
    role: "恋爱漫爱好者",
    text: "收录范围很全，目录一眼看清，最新话标记得明明白白，再也不用担心追丢更新了。",
  },
  {
    name: "老猫",
    role: "悬疑推理迷",
    text: "悬疑分类的漫画质量都很高，评分也很靠谱，基本跟着排行榜选就不会踩雷。",
  },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setFormError("");

    // Anti-spam honeypot check
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到您的留言，我们会尽快回复，感谢关注漫画网！");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    try {
      const res = await fetch(
        "https://readdy.ai/api/form/da0kos83256a3o8kou1g",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const serverBody = serverMsg.toLowerCase();
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage("已收到您的留言，我们会尽快回复，感谢关注漫画网！");
        form.reset();
      } else if (!res.ok || serverBody.includes("spam")) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试");
    }
  };

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于漫画网",
          url: `${site}/about`,
          inLanguage: "zh-CN",
          description: "关于漫画网品牌介绍、读者口碑与联系方式。",
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${site}/` },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        }}
      />

      <section className="bg-secondary-100/70 pb-10 pt-24 md:pt-28">
        <div className="container-x">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-5 font-display text-2xl font-black text-foreground-900 md:text-4xl">关于漫画网</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
            用心做漫画大全收录的阅读平台，让每一位漫画爱好者都能快速找到想看的作品。
          </p>
        </div>
      </section>

      {/* Brand intro */}
      <section className="bg-background-50 py-12 md:py-16" aria-labelledby="about-heading">
        <div className="container-x grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-building-2-line"></i> 品牌介绍
            </span>
            <h2 id="about-heading" className="mt-3 font-display text-2xl font-black text-foreground-900 md:text-3xl">
              一站式漫画网漫画大全平台
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              漫画网是一个面向中文读者的漫画收录与阅读导航平台。我们以「漫画网漫画库、漫画网漫画分类、漫画网漫画目录」三位一体的组织方式，将海量漫画作品系统化地收录整理，帮助读者绕过繁琐的搜索，直接进入列表快速定位心仪的作品。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              无论你钟爱热血战斗、甜蜜恋爱、烧脑悬疑、瑰丽奇幻、硬核科幻还是轻松搞笑，漫画网都能在六大分类中为你找到值得一看的漫画，并持续同步最新章节。
            </p>
            <ul className="mt-5 space-y-2.5 text-sm text-foreground-700">
              {["分类清晰 · 速查便捷", "目录完整 · 更新及时", "收录丰富 · 内容多元"].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-primary-500"></i> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative overflow-hidden rounded-lg border border-background-200/70">
            <img
              src="https://readdy.ai/api/search-image?query=creative%20workspace%20of%20a%20manga%20reading%20platform%20with%20open%20comic%20books%20and%20laptop%2C%20warm%20vermillion%20and%20golden%20desk%20lighting%2C%20cozy%20artistic%20atmosphere%2C%20clean%20stylized%20composition&width=1200&height=800&seq=about-brand&orientation=landscape"
              alt="漫画网品牌介绍 - 漫画阅读平台"
              className="h-full w-full object-cover"
            />
          </div>
        </div>

        {/* Values */}
        <div className="container-x mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {values.map((v) => (
            <div key={v.label} className="rounded-lg border border-background-200/70 bg-background-50 p-5 text-center">
              <i className={`${v.icon} text-2xl text-primary-500`}></i>
              <p className="mt-3 font-display text-2xl font-black text-foreground-900">{v.num}</p>
              <p className="mt-1 text-sm font-semibold text-foreground-800">{v.label}</p>
              <p className="mt-0.5 text-xs text-foreground-500">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reputation */}
      <section className="bg-background-100/70 py-12 md:py-16" aria-labelledby="testimonial-heading">
        <div className="container-x">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              <i className="ri-double-quotes-l"></i> 读者口碑
            </span>
            <h2 id="testimonial-heading" className="mt-3 font-display text-2xl font-black text-foreground-900 md:text-3xl">
              被读者信赖的漫画平台
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200/70 bg-background-50 p-6">
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-user-smile-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-background-50 py-12 md:py-16" aria-labelledby="contact-heading">
        <div className="container-x grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-customer-service-2-line"></i> 联系方式
            </span>
            <h2 id="contact-heading" className="mt-3 font-display text-2xl font-black text-foreground-900 md:text-3xl">
              与我们取得联系
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              无论是推荐漫画、反馈问题还是商务合作，欢迎随时联系我们，我们会尽快回复。
            </p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="font-semibold text-foreground-900">邮箱</p>
                  <p className="text-foreground-600">contact@manhua-wang.example</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <div>
                  <p className="font-semibold text-foreground-900">电话</p>
                  <p className="text-foreground-600">400-000-0000（工作日 9:00-18:00）</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-map-pin-2-line text-lg"></i>
                </span>
                <div>
                  <p className="font-semibold text-foreground-900">地址</p>
                  <p className="text-foreground-600">北京市朝阳区漫画产业园 A 座 8 层</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Contact form */}
          <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-6 md:p-8">
            <h3 className="text-lg font-bold text-foreground-900">给我们留言</h3>
            <p className="mt-1 text-sm text-foreground-600">填写以下表单，我们会尽快与你联系</p>
            <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的称呼"
                    className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  placeholder="例如：推荐漫画 / 反馈问题"
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请输入您的留言内容（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>
              {/* Anti-spam honeypot */}
              <div className="hp-alt-field" aria-hidden="true">
                <label htmlFor="website_alt">网站地址</label>
                <input id="website_alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              {status === "success" && (
                <p className="rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-fill mr-1"></i> {message}
                </p>
              )}
              {status === "error" && formError && (
                <p className="rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-error-warning-fill mr-1"></i> {formError}
                </p>
              )}

              <button
                type="submit"
                className="w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                提交留言
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}